import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { ASSISTANT } from "@/constants/testIds";
import { Send, Bot, User } from "lucide-react";

export default function Assistant() {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: text,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Assistant temporarily unavailable." }]);
    } finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div data-testid={ASSISTANT.root} className="flex flex-col h-screen">
      <TopBar title={t("assistant.title")} subtitle={t("assistant.subtitle")} />
      <div className="flex-1 overflow-y-auto px-6 md:px-10 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.length === 0 && (
            <div className="text-center py-16 text-sm text-[#5B5B62]">
              <Bot className="h-8 w-8 text-[#0B2545] mx-auto mb-4" strokeWidth={1.5} />
              <div className="font-display text-lg text-[#1F2937]">ALTIORA</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em]">
                {lang === "fr" ? "coaching · analyse · génération · matching" : "coaching · analysis · generation · matching"}
              </div>
              <div className="mt-6">{lang === "fr" ? "Posez votre première question stratégique." : "Ask your first strategic question."}</div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} data-testid={ASSISTANT.message}
                 className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ${
                msg.role === "user"
                  ? "border-[#B8B5AC] bg-[#EBE8DE] text-[#1F2937]"
                  : "border-[#0B2545]/40 bg-[#0B2545]/8 text-[#0B2545]"
              }`}>
                {msg.role === "user"
                  ? <User className="h-4 w-4" strokeWidth={1.5} />
                  : <Bot className="h-4 w-4" strokeWidth={1.5} />}
              </div>
              <div className={`max-w-[80%] rounded-md px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === "user"
                  ? "bg-[#EBE8DE] border border-[#E4E1D8] text-[#1F2937]"
                  : "bg-white border border-[#E4E1D8] text-[#E2E8F0]"
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-md border border-[#0B2545]/40 bg-[#0B2545]/8 text-[#0B2545] flex items-center justify-center">
                <Bot className="h-4 w-4" strokeWidth={1.5} />
              </div>
              <div className="rounded-md border border-[#E4E1D8] bg-white px-4 py-3">
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 bg-[#0B2545] rounded-full animate-pulse" />
                  <span className="h-1.5 w-1.5 bg-[#0B2545] rounded-full animate-pulse" style={{ animationDelay: "0.2s" }} />
                  <span className="h-1.5 w-1.5 bg-[#0B2545] rounded-full animate-pulse" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <div className="border-t border-[#E4E1D8] bg-[#F8F7F3] px-6 md:px-10 py-4">
        <div className="max-w-3xl mx-auto flex items-end gap-3">
          <textarea
            data-testid={ASSISTANT.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder={t("assistant.placeholder")}
            className="flex-1 resize-none rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-4 py-3 text-sm text-[#1F2937] font-body"
          />
          <button
            data-testid={ASSISTANT.send}
            onClick={send} disabled={busy || !input.trim()}
            className="rounded-md bg-[#0B2545] hover:bg-[#DCC29A] disabled:opacity-40 text-white p-3 transition-colors"
          >
            <Send className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
